import { useEffect, useRef, useState } from 'react'
import { Paperclip, Send, X } from 'lucide-react'
import { toast } from 'sonner'
import { useUploadAttachment, useUserSearch } from '../hooks'
import { isImage } from '../attachments'
import type { CollabUser } from '../types'

// Wyłapuje "@query" tuż przed kursorem (query bez spacji), żeby wiedzieć co podpowiadać.
const MENTION_RE = /(?:^|\s)@(\S{2,})$/

function getMentionQuery(textBeforeCaret: string): string | null {
  const m = MENTION_RE.exec(textBeforeCaret)
  return m ? m[1] : null
}

const EXT_BY_TYPE: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
}

function extFromType(type: string): string {
  return EXT_BY_TYPE[type] ?? type.split('/')[1] ?? 'bin'
}

/**
 * Wklejony z clipboardu obraz często nie ma nazwy (lub generyczne "image.png" w Chrome),
 * co przy kilku wklejeniach koliduje - nadajemy unikalną nazwę po `seq`.
 */
function ensureNamed(file: File, seq: number): File {
  const name = file.name?.trim()
  if (name && name !== 'image.png') return file
  return new File([file], `wklejony-obraz-${seq}.${extFromType(file.type)}`, { type: file.type })
}

type Pending = {
  tempId: number
  name: string
  status: 'uploading' | 'done' | 'error'
  id?: number
  previewUrl?: string  // dla obrazów: URL.createObjectURL (zwalniany przy usunięciu/wyczyszczeniu)
}

export function Composer({ onSend, disabled }: {
  onSend: (input: { body: string; mentions: number[]; attachmentIds: number[] }) => void
  disabled?: boolean
}) {
  const [body, setBody] = useState('')
  const [mentions, setMentions] = useState<number[]>([])
  const [query, setQuery] = useState('')
  const [caret, setCaret] = useState(0)
  const [active, setActive] = useState(0)
  const [pending, setPending] = useState<Pending[]>([])
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const tempIdSeq = useRef(0)

  const upload = useUploadAttachment()
  const { data: results } = useUserSearch(query)
  const items = results ?? []
  const showDropdown = query.length >= 2 && items.length > 0

  const uploading = pending.some(p => p.status === 'uploading')
  const doneIds = pending.filter(p => p.status === 'done' && p.id != null).map(p => p.id!)
  const canSend = !disabled && !uploading && (body.trim().length > 0 || doneIds.length > 0)

  // Reset zaznaczenia przy zmianie listy podpowiedzi.
  useEffect(() => {
    setActive(0)
  }, [query])

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const value = e.target.value
    const pos = e.target.selectionStart ?? value.length
    setBody(value)
    setCaret(pos)
    setQuery(getMentionQuery(value.slice(0, pos)) ?? '')
  }

  function pick(user: CollabUser) {
    // Zamień "@query" tuż przed kursorem na "@Imię Nazwisko " (zachowujemy @,
    // żeby MessageItem mógł podświetlić wzmiankę).
    const before = body.slice(0, caret)
    const after = body.slice(caret)
    const replaced = before.replace(MENTION_RE, (whole, q: string) => {
      const lead = whole.slice(0, whole.length - q.length - 1) // zachowaj wiodącą spację/początek
      return `${lead}@${user.fullName} `
    })
    const next = replaced + after
    setBody(next)
    setMentions(prev => (prev.includes(user.id) ? prev : [...prev, user.id]))
    setQuery('')
    const newCaret = replaced.length
    setCaret(newCaret)
    requestAnimationFrame(() => {
      const ta = textareaRef.current
      if (ta) {
        ta.focus()
        ta.setSelectionRange(newCaret, newCaret)
      }
    })
  }

  async function addFiles(files: File[]) {
    for (const raw of files) {
      const tempId = ++tempIdSeq.current
      const file = ensureNamed(raw, tempId)
      const previewUrl = isImage(file.type) ? URL.createObjectURL(file) : undefined
      setPending(prev => [...prev, { tempId, name: file.name, status: 'uploading', previewUrl }])
      try {
        const res = await upload.mutateAsync(file)
        setPending(prev => prev.map(p =>
          p.tempId === tempId ? { ...p, status: 'done', id: res.id } : p))
      } catch {
        setPending(prev => prev.map(p =>
          p.tempId === tempId ? { ...p, status: 'error' } : p))
        toast.error(`Nie udało się wysłać ${file.name}`)
      }
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const dt = e.clipboardData
    // Część przeglądarek wystawia wklejony obraz tylko w items (nie w files).
    let files = Array.from(dt.files)
    if (files.length === 0 && dt.items) {
      files = Array.from(dt.items)
        .filter(it => it.kind === 'file')
        .map(it => it.getAsFile())
        .filter((f): f is File => f != null)
    }
    if (files.length > 0) {
      e.preventDefault() // nie wstawiaj surowego tekstu/ścieżki, tylko upload
      addFiles(files)
    }
  }

  function removePending(tempId: number) {
    setPending(prev => {
      const p = prev.find(x => x.tempId === tempId)
      if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl)
      return prev.filter(x => x.tempId !== tempId)
    })
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (showDropdown) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActive(a => (a + 1) % items.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActive(a => (a - 1 + items.length) % items.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        pick(items[active]!)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setQuery('')
        return
      }
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      // Ctrl/Cmd+Enter wysyła wiadomość.
      e.preventDefault()
      send()
    }
  }

  function send() {
    if (!canSend) return
    onSend({ body, mentions, attachmentIds: doneIds })
    pending.forEach(p => { if (p.previewUrl) URL.revokeObjectURL(p.previewUrl) })
    setBody('')
    setMentions([])
    setQuery('')
    setCaret(0)
    setPending([])
  }

  return (
    <div className="relative space-y-2 border-t p-2">
      {pending.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {pending.map(p => (
            <div
              key={p.tempId}
              className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs"
            >
              {p.previewUrl ? (
                <img src={p.previewUrl} alt="" className="h-6 w-6 rounded object-cover" />
              ) : (
                <Paperclip className="h-3.5 w-3.5 text-slate-400" />
              )}
              <span className="max-w-[12rem] truncate text-slate-700">{p.name}</span>
              {p.status === 'uploading' && <span className="text-slate-400">…</span>}
              {p.status === 'error' && <span className="text-rose-600">błąd</span>}
              <button
                type="button"
                onClick={() => removePending(p.tempId)}
                aria-label={`Usuń załącznik ${p.name}`}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <textarea
        ref={textareaRef}
        value={body}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        disabled={disabled}
        rows={3}
        placeholder="Napisz wiadomość…"
        className="w-full resize-none rounded-md border border-slate-200 px-2.5 py-2 text-sm focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-400 disabled:bg-slate-100"
      />

      {showDropdown && (
        <ul className="absolute bottom-14 left-2 right-2 z-10 max-h-48 overflow-auto rounded border bg-white text-sm shadow-lg">
          {items.map((u, i) => (
            <li key={u.id}>
              <button
                type="button"
                onMouseDown={e => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(u)}
                className={[
                  'block w-full px-3 py-1.5 text-left',
                  i === active ? 'bg-sky-100 text-sky-800' : 'hover:bg-sky-50',
                ].join(' ')}
              >
                {u.fullName}
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={e => {
          addFiles(Array.from(e.target.files ?? []))
          e.target.value = ''
        }}
      />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            aria-label="Dodaj załącznik"
            className="inline-flex items-center text-slate-400 transition-colors hover:text-slate-600 disabled:opacity-50"
          >
            <Paperclip className="h-4 w-4" />
          </button>
          <span className="text-[11px] text-slate-400">@ aby wspomnieć · Ctrl+Enter wysyła</span>
        </div>
        <button
          type="button"
          onClick={send}
          disabled={!canSend}
          className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 px-3.5 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
        >
          <Send className="h-3.5 w-3.5" />
          Wyślij
        </button>
      </div>
    </div>
  )
}
